import { useCallback, useEffect, useState } from 'react';

// An app runs in two environments, recorded under different app ids:
//   test  the draft app (console test chat, test keys)   <appId>
//   live  the published app (live widgets, live keys)    <appId>_live
export const ENV_TEST = 'test';
export const ENV_LIVE = 'live';

const STORAGE_PREFIX = 'bc.environment.';
const CHANGE_EVENT = 'bc-app-environment-change';

export function environmentAppId(appId, env) {
    if (!appId) return appId;
    return env === ENV_LIVE ? `${appId}_live` : appId;
}

function readStored(appId) {
    try {
        const value = window.localStorage.getItem(STORAGE_PREFIX + appId);
        return value === ENV_LIVE ? ENV_LIVE : ENV_TEST;
    } catch {
        return ENV_TEST;
    }
}

/**
 * The environment the user is looking at for this app, shared by Analytics,
 * Traces and Conversations: picking Live on one page carries over to the
 * others, and survives a reload. Defaults to Test.
 *
 * Returns { env, setEnv, envAppId }, where envAppId is the id to send to the
 * APIs for that environment.
 */
export function useAppEnvironment(appId) {
    const [env, setEnvState] = useState(() => readStored(appId));

    useEffect(() => {
        setEnvState(readStored(appId));
        // Other components on the page using this hook follow a change.
        const onChange = event => {
            if (event.detail?.appId === appId) setEnvState(event.detail.env);
        };
        window.addEventListener(CHANGE_EVENT, onChange);
        return () => window.removeEventListener(CHANGE_EVENT, onChange);
    }, [appId]);

    const setEnv = useCallback(next => {
        const value = next === ENV_LIVE ? ENV_LIVE : ENV_TEST;
        try {
            window.localStorage.setItem(STORAGE_PREFIX + appId, value);
        } catch {
            // Storage blocked: the choice still applies until the page reloads.
        }
        setEnvState(value);
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { appId, env: value } }));
    }, [appId]);

    return { env, setEnv, envAppId: environmentAppId(appId, env) };
}
